/**
 * 暖棕摩卡模板 - 列表页
 *
 * 设计理念：咖啡馆质感 + 商务杂志目录
 * - 吸顶分类 tab 横条（无侧边栏，区别于 red 左侧筛选）
 * - 双列卡片网格 + 数字分页
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/templates/mocha/components/Navbar";
import Footer from "@/templates/mocha/components/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "popular", label: "最多浏览", icon: "ri-fire-line" },
  { key: "alpha", label: "按名称", icon: "ri-sort-alphabet-asc" },
] as const;

export default function MochaCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    const params: Record<string, any> = { page: currentPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, currentPage]);

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    if (sortBy === "alpha") list.sort((a, b) => a.title.localeCompare(b.title, "zh"));
    return list;
  }, [listings, sortBy]);

  const displayCategories = navCategories.length > 0 ? navCategories : categories;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-[#FAF6F0]">
      <Navbar />

      {/* 面包屑 */}
      <div className="bg-white border-b border-[#F0E6D6] pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <nav className="flex items-center gap-1.5 text-sm">
            <Link to="/" className="text-[#8A7B6D] hover:text-[#C77B3F] transition-colors flex items-center gap-1">
              <i className="ri-home-4-line" /> 首页
            </Link>
            <i className="ri-arrow-right-s-line text-xs text-[#DCCFB8]" />
            {activeCategoryId ? (
              <>
                <Link to="/category" className="text-[#8A7B6D] hover:text-[#C77B3F] transition-colors">全部分类</Link>
                <i className="ri-arrow-right-s-line text-xs text-[#DCCFB8]" />
                <span className="text-[#C77B3F] font-bold">{currentCategoryName}</span>
              </>
            ) : (
              <span className="text-[#C77B3F] font-bold">全部分类</span>
            )}
            <span className="ml-2 text-xs text-[#B7A892]">共 {total} 条</span>
          </nav>
        </div>
      </div>

      {/* ================= 吸顶分类 tab 横条 ================= */}
      <div className="sticky top-14 md:top-16 z-30 bg-[#FAF6F0]/95 backdrop-blur border-b border-[#E8DCC8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-1.5 overflow-x-auto py-2.5 scrollbar-hide">
            <button
              onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer border ${
                !activeCategoryId
                  ? "bg-[#4A3525] text-[#F5EBDD] border-[#4A3525] font-bold"
                  : "bg-white text-[#4A3525] border-[#E8DCC8] hover:border-[#C77B3F] hover:text-[#C77B3F]"
              }`}
            >
              <i className="ri-global-line mr-1" />全部分类
            </button>
            {displayCategories.slice(0, 15).map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id);
                  setCurrentPage(1);
                }}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer border ${
                  activeCategoryId === cat.id
                    ? "bg-[#4A3525] text-[#F5EBDD] border-[#4A3525] font-bold"
                    : "bg-white text-[#4A3525] border-[#E8DCC8] hover:border-[#C77B3F] hover:text-[#C77B3F]"
                }`}
              >
                <i className={`${cat.icon || "ri-folder-line"} mr-1`} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* 工具栏 */}
        <div className="flex items-center justify-between mb-5 bg-white rounded-lg border border-[#F0E6D6] px-4 py-2.5">
          <h2 className="text-sm font-bold text-[#4A3525] flex items-center gap-2">
            <i className="ri-folder-fill text-[#C77B3F]" />
            {currentCategoryName}
          </h2>
          <div className="flex items-center gap-1 bg-[#FAF6F0] rounded-lg p-0.5">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer ${
                  sortBy === opt.key ? "bg-[#4A3525] text-[#F5EBDD] shadow-sm" : "text-[#8A7B6D] hover:text-[#4A3525]"
                }`}
              >
                <i className={`${opt.icon} text-[11px]`} />
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── 双列卡片网格 ── */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg border border-[#F0E6D6] p-4 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-20 h-20 bg-[#F0E6D6] rounded-lg shrink-0" />
                  <div className="flex-1 space-y-2.5">
                    <div className="w-2/3 h-4 bg-[#F0E6D6] rounded" />
                    <div className="w-1/2 h-3 bg-[#FAF6F0] rounded" />
                    <div className="w-24 h-3 bg-[#FAF6F0] rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : sortedListings.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sortedListings.map((listing) => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className={`group bg-white rounded-lg border border-[#F0E6D6] hover:border-[#C77B3F]/50 hover:shadow-md transition-all p-4 ${
                    listing.is_pinned ? "border-l-4 border-l-[#C77B3F]" : ""
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* 缩略图：aspect-square */}
                    <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-[#FAF6F0] flex items-center justify-center">
                      {listing.images?.[0] ? (
                        <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <i className="ri-image-line text-[#DCCFB8] text-2xl" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {listing.is_pinned && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-[#C77B3F] text-white rounded font-bold shrink-0">顶</span>
                        )}
                        <h3 className="text-sm font-bold text-[#2B241D] group-hover:text-[#C77B3F] transition-colors truncate">
                          {listing.title}
                        </h3>
                      </div>
                      <p className="text-xs text-[#8A7B6D] mt-1.5 line-clamp-2 leading-relaxed">
                        {listing.content?.substring(0, 90) || "暂无描述"}
                      </p>
                      <div className="flex items-center gap-2 mt-2.5 text-[11px] text-[#8A7B6D]">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FAF6F0] text-[#B0672F] rounded-full">
                          <i className={`${listing.category?.icon || 'ri-folder-line'} text-[10px]`} />
                          {listing.category?.name || "未分类"}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-eye-line" />{listing.view_count || 0}
                        </span>
                        <span className="ml-auto inline-flex items-center gap-1 text-[#B7A892]">
                          <i className="ri-time-line" />{formatTableDate(listing.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-4 text-xs text-[#8A7B6D]">
              第 {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, total)} 条，共 {total} 条
            </div>
          </>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg border border-[#F0E6D6]">
            <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-[#FAF6F0]">
              <i className="ri-inbox-line text-xl text-[#C77B3F]" />
            </div>
            <p className="text-[#4A3525] font-medium">该分类暂无信息</p>
            <p className="text-[#8A7B6D] text-sm mt-1">换个分类试试</p>
          </div>
        )}

        {/* 分页 */}
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#E8DCC8] bg-white text-[#8A7B6D] hover:border-[#C77B3F] hover:text-[#C77B3F] disabled:opacity-30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {generatePageNumbers(currentPage, totalPages).map((p, i) =>
              typeof p === "number" ? (
                <button
                  key={i}
                  onClick={() => setCurrentPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                    currentPage === p ? "bg-[#4A3525] text-[#F5EBDD]" : "border border-[#E8DCC8] bg-white text-[#4A3525] hover:border-[#C77B3F]"
                  }`}
                >
                  {p}
                </button>
              ) : (
                <span key={i} className="w-9 h-9 flex items-center justify-center text-[#B7A892] text-xs">...</span>
              )
            )}

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-[#E8DCC8] bg-white text-[#8A7B6D] hover:border-[#C77B3F] hover:text-[#C77B3F] disabled:opacity-30 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function formatTableDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}天前`;
  return `${d.getMonth() + 1}-${d.getDate()}`;
}

function generatePageNumbers(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push("...");
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push("...");
    pages.push(total);
  }
  return pages;
}
